import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Question {
  id: string;
  question_text: string;
  question_type: string;
  hint: string;
  display_order: number;
}

interface Scenario {
  id: string;
  scenario_name: string;
  difficulty: string;
  context: string;
  form_data: any;
  form_number: string;
  form_name: string;
  box_definitions: any;
  questions: Question[];
}

interface SubmitResult {
  questionId: string;
  userAnswer: string;
  isCorrect: boolean;
  correctAnswer: string;
  explanation: string;
}

const DocumentExercisePage: React.FC = () => {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [showHint, setShowHint] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState<SubmitResult[]>([]);
  const [score, setScore] = useState<number>(0);
  const [totalQuestions, setTotalQuestions] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (scenarioId) {
      fetchScenario();
    }
  }, [scenarioId]);

  const fetchScenario = async () => {
    try {
      const response = await api.get(`/documents/scenarios/${scenarioId}`);
      setScenario(response.data);
      setTotalQuestions(response.data.questions.length);
    } catch (error) {
      console.error('Error fetching scenario:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    if (currentQuestionIndex < (scenario?.questions.length || 0) - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowHint(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowHint(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await api.post(
        `/documents/scenarios/${scenarioId}/submit`,
        { answers }
      );
      
      setResults(response.data.results);
      setScore(response.data.score);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting answers:', error);
    }
  };

  const renderW2Form = () => {
    if (!scenario) return null;
    const data = scenario.form_data;

    return (
      <div className="bg-white text-black p-6 rounded-lg border-2 border-slate-300">
        <div className="text-center font-bold text-lg mb-4 border-b-2 border-black pb-2">
          Form W-2 - Wage and Tax Statement - 2024
        </div>

        {/* Employer Info */}
        <div className="mb-4 pb-4 border-b border-slate-300">
          <div className="text-xs font-semibold mb-1">Employer</div>
          <div className="font-semibold">{data.employer_name}</div>
          <div className="text-sm">EIN: {data.employer_ein}</div>
        </div>

        {/* Employee Info */}
        <div className="mb-4 pb-4 border-b border-slate-300">
          <div className="text-xs font-semibold mb-1">Employee</div>
          <div className="font-semibold">{data.employee_name}</div>
          <div className="text-sm">SSN: {data.employee_ssn}</div>
        </div>

        {/* Main Boxes */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Box 1 */}
          <div className="border border-slate-400 p-2 bg-slate-50">
            <div className="text-xs font-semibold">1. Wages, tips, other compensation</div>
            <div className="text-lg font-bold">${parseFloat(data.box_1).toLocaleString()}</div>
          </div>

          {/* Box 2 */}
          <div className="border border-slate-400 p-2 bg-slate-50">
            <div className="text-xs font-semibold">2. Federal income tax withheld</div>
            <div className="text-lg font-bold">${parseFloat(data.box_2).toLocaleString()}</div>
          </div>

          {/* Box 3 */}
          <div className="border border-slate-400 p-2 bg-slate-50">
            <div className="text-xs font-semibold">3. Social Security wages</div>
            <div className="text-lg font-bold">${parseFloat(data.box_3).toLocaleString()}</div>
          </div>

          {/* Box 4 */}
          <div className="border border-slate-400 p-2 bg-slate-50">
            <div className="text-xs font-semibold">4. Social Security tax withheld</div>
            <div className="text-lg font-bold">${parseFloat(data.box_4).toLocaleString()}</div>
          </div>

          {/* Box 5 */}
          <div className="border border-slate-400 p-2 bg-slate-50">
            <div className="text-xs font-semibold">5. Medicare wages and tips</div>
            <div className="text-lg font-bold">${parseFloat(data.box_5).toLocaleString()}</div>
          </div>

          {/* Box 6 */}
          <div className="border border-slate-400 p-2 bg-slate-50">
            <div className="text-xs font-semibold">6. Medicare tax withheld</div>
            <div className="text-lg font-bold">${parseFloat(data.box_6).toLocaleString()}</div>
          </div>
        </div>

        {/* Box 12 */}
        {(data.box_12a_code || data.box_12b_code || data.box_12c_code) && (
          <div className="mb-4 border border-slate-400 p-2 bg-slate-50">
            <div className="text-xs font-semibold mb-2">12. See instructions for box 12</div>
            <div className="grid grid-cols-3 gap-2">
              {data.box_12a_code && (
                <div>
                  <span className="font-semibold">{data.box_12a_code}:</span> ${parseFloat(data.box_12a_amount).toLocaleString()}
                </div>
              )}
              {data.box_12b_code && (
                <div>
                  <span className="font-semibold">{data.box_12b_code}:</span> ${parseFloat(data.box_12b_amount).toLocaleString()}
                </div>
              )}
              {data.box_12c_code && (
                <div>
                  <span className="font-semibold">{data.box_12c_code}:</span> ${parseFloat(data.box_12c_amount).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Box 13 */}
        {(data.box_13_statutory || data.box_13_retirement || data.box_13_sick_pay) && (
          <div className="border border-slate-400 p-2 bg-slate-50">
            <div className="text-xs font-semibold mb-2">13. Checkboxes</div>
            <div className="space-y-1">
              {data.box_13_statutory && <div>☑ Statutory employee</div>}
              {data.box_13_retirement && <div>☑ Retirement plan</div>}
              {data.box_13_sick_pay && <div>☑ Third-party sick pay</div>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderResults = () => {
    if (!submitted || !scenario) return null;

    const currentResult = results[currentQuestionIndex];

    return (
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${currentResult.isCorrect ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{currentResult.isCorrect ? '✅' : '❌'}</span>
            <span className="font-semibold text-lg">
              {currentResult.isCorrect ? 'Correct!' : 'Incorrect'}
            </span>
          </div>
          
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-slate-400">Your answer:</span>
              <span className="ml-2 font-semibold">{currentResult.userAnswer || '(No answer)'}</span>
            </div>
            {!currentResult.isCorrect && (
              <div>
                <span className="text-slate-400">Correct answer:</span>
                <span className="ml-2 font-semibold text-green-400">{currentResult.correctAnswer}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-blue-500/20 border border-blue-500/30 p-4 rounded-lg">
          <div className="font-semibold mb-2">💡 Explanation</div>
          <p className="text-sm text-slate-300">{currentResult.explanation}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-xl text-white">Loading exercise...</div>
      </div>
    );
  }

  if (!scenario) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-xl text-white">Scenario not found</div>
      </div>
    );
  }

  const currentQuestion = scenario.questions[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-primary-500/20 bg-slate-900/95 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="text-slate-400 hover:text-white transition-colors"
            >
              ← Back
            </button>
            <div className="text-sm text-slate-400">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Scenario Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">{scenario.scenario_name}</h1>
          <p className="text-slate-400">{scenario.context}</p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Form */}
          <div>
            <h2 className="text-xl font-bold mb-4">Document</h2>
            {renderW2Form()}
          </div>

          {/* Right: Questions */}
          <div>
            <h2 className="text-xl font-bold mb-4">Questions</h2>
            
            <div className="card">
              <div className="mb-4">
                <div className="text-lg font-semibold mb-4">{currentQuestion.question_text}</div>

                {!submitted && (
                  <input
                    type="text"
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500"
                    placeholder="Enter your answer..."
                  />
                )}

                {submitted && renderResults()}
              </div>

              {!submitted && (
                <div>
                  <button
                    onClick={() => setShowHint(!showHint)}
                    className="text-sm text-primary-400 hover:text-primary-300 mb-4"
                  >
                    💡 {showHint ? 'Hide' : 'Show'} Hint
                  </button>

                  {showHint && (
                    <div className="bg-blue-500/20 border border-blue-500/30 p-3 rounded-lg mb-4 text-sm">
                      {currentQuestion.hint}
                    </div>
                  )}
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Previous
                </button>

                {!submitted ? (
                  currentQuestionIndex === totalQuestions - 1 ? (
                    <button
                      onClick={handleSubmit}
                      className="px-6 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg font-semibold transition-colors"
                    >
                      Submit All Answers
                    </button>
                  ) : (
                    <button
                      onClick={handleNext}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                    >
                      Next
                    </button>
                  )
                ) : (
                  currentQuestionIndex < totalQuestions - 1 ? (
                    <button
                      onClick={handleNext}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(-1)}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition-colors"
                    >
                      Complete ({score}/{totalQuestions})
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Score Summary (shown after submission) */}
            {submitted && (
              <div className="card mt-6">
                <h3 className="font-bold text-lg mb-4">Your Results</h3>
                <div className="text-3xl font-bold mb-2">
                  {score}/{totalQuestions}
                  <span className="text-lg text-slate-400 ml-2">
                    ({Math.round((score / totalQuestions) * 100)}%)
                  </span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3 mb-4">
                  <div
                    className="bg-gradient-to-r from-green-500 to-green-400 h-3 rounded-full transition-all"
                    style={{ width: `${(score / totalQuestions) * 100}%` }}
                  />
                </div>
                {score === totalQuestions && (
                  <div className="text-green-400 font-semibold">🎉 Perfect score!</div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocumentExercisePage;
